import React, { useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { AddressValue, Question, FormField } from '../../../types';
import FormFieldInput from './FormFieldInput';
import QuestionHelpText from '../QuestionHelpText';

interface FormEntryRendererProps {
  question: Question;
  selectedValues: Record<string, string | AddressValue>;
  onSelect: (values: Record<string, string | AddressValue>) => void;
  onValidityChange?: (isValid: boolean) => void;
}

const requiredParts: Array<Exclude<keyof AddressValue, 'country'>> = ['addressLine1', 'city', 'stateProvince', 'postalCode'];

function FormEntryRenderer({
  question,
  selectedValues,
  onSelect,
  onValidityChange,
}: FormEntryRendererProps): JSX.Element {
  const formFields = question.formFields || [];
  const [prefillEnabled, setPrefillEnabled] = useState<Record<number, boolean>>({});
  const [blurredFields, setBlurredFields] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setPrefillEnabled({});
    setBlurredFields({});
  }, [question.id]);

  useEffect(() => {
    const updates: Record<string, AddressValue> = {};
    formFields.forEach((field) => {
      if (field.type !== 'address' || field.prefillFromFieldId === undefined || !prefillEnabled[field.id]) return;
      const targetKey = `field_${field.id}`;
      const source = selectedValues[`field_${field.prefillFromFieldId}`];
      if (isAddressValue(source) && JSON.stringify(selectedValues[targetKey]) !== JSON.stringify(source)) updates[targetKey] = source;
    });
    if (Object.keys(updates).length > 0) onSelect({ ...selectedValues, ...updates });
  }, [formFields, onSelect, prefillEnabled, selectedValues]);

  const handleFieldChange = (fieldId: number, value: string | AddressValue): void => {
    const fieldKey = `field_${fieldId}`;
    const nextValues = { ...selectedValues };
    if (typeof value === 'string' && !value.trim()) {
      delete nextValues[fieldKey];
    } else {
      nextValues[fieldKey] = value;
    }
    onSelect(nextValues);
  };

  const handleFieldBlur = (fieldId: number): void => {
    setBlurredFields((current) => current[fieldId] ? current : { ...current, [fieldId]: true });
  };

  const validateField = (field: FormField, value: string | AddressValue, blurred?: boolean): string | null => {
    if (field.isRequired && blurred) {
      if (typeof value === 'string' && !value.trim()) return `${field.label} is required`;
      if (typeof value === 'object' && !requiredParts.every((part) => value[part].trim())) return `${field.label} is required`;
    }

    switch (field.type) {
      case 'email': {
        const email = typeof value === 'string' ? value.trim() : '';
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
          return `${field.label} must be a valid email address`;
        break;
      }
      case 'phone': {
        const phone = typeof value === 'string' ? value.trim() : '';
        if (phone && !/^\d{3}-\d{3}-\d{4}$/.test(phone))
          return `${field.label} must be a valid phone number`;
        break;
      }
      default:
        break;
    }

    if (typeof value === 'string' && value && field.validation) {
      if (field.validation.minLength && value.length < field.validation.minLength)
        return `${field.label} must be at least ${field.validation.minLength} characters`;
      if (field.validation.maxLength && value.length > field.validation.maxLength)
        return `${field.label} must be no more than ${field.validation.maxLength} characters`;
    }

    return null;
  };

  const isFormValid = formFields.every((field) => {
    const fieldKey = `field_${field.id}`;
    const value = selectedValues[fieldKey] ?? '';
    return validateField(field, value, blurredFields[field.id]) === null;
  });

  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  const inputSx = {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#e0e0e0', borderRadius: '12px' },
    '&:hover fieldset': { borderColor: '#b0b0b0' },
    '&.Mui-focused fieldset': { borderColor: '#00A9B5' },
  };

  return (
    <Box className="cla-quiz-form" sx={{ mt: 1 }}>
      <QuestionHelpText description={question.metadata?.description} />
      <Typography
        className="cla-quiz-instruction"
        variant="body2"
        sx={{ mb: 3, fontStyle: 'italic', color: '#888', fontSize: '0.82rem' }}
      >
        Text entry — fields marked * are required
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, rowGap: 0, columnGap: '24px' }}>
        {formFields.map((field) => {
          const fieldKey = `field_${field.id}`;
          const value = selectedValues[fieldKey] ?? '';
          const isBlurred = blurredFields[field.id] ?? false;
          const error = validateField(field, value, isBlurred);

          return (
            <Box className="cla-quiz-field-group" data-quiz-field-id={field.id} data-quiz-field-name={fieldKey} key={field.id}>
              {field.type === 'address' && field.prefillFromFieldId !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography className="cla-quiz-field-label" component="label" htmlFor={`form-field-${field.id}`} sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1C3654' }}>
                    {field.label}{field.isRequired ? ' *' : ''}
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={prefillEnabled[field.id] ?? false}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setPrefillEnabled((current) => ({ ...current, [field.id]: enabled }));
                          if (enabled) {
                            const source = selectedValues[`field_${field.prefillFromFieldId}`];
                            if (isAddressValue(source)) handleFieldChange(field.id, source);
                          }
                        }}
                        sx={{ p: 0.25 }}
                      />
                    )}
                    label={<Typography variant="caption">Same as {formFields.find((candidate) => candidate.id === field.prefillFromFieldId)?.label || 'previous address'}?</Typography>}
                    sx={{ mr: 0, ml: 1, my: 0 }}
                  />
                </Box>
              )}
              {!(field.type === 'address' && field.prefillFromFieldId !== undefined) && (
                <Typography className="cla-quiz-field-label" component="label" htmlFor={`form-field-${field.id}`} sx={{ display: 'block', mb: 0.75, fontSize: '0.875rem', fontWeight: 500, color: '#1C3654' }}>
                  {field.label}{field.isRequired ? ' *' : ''}
                </Typography>
              )}
              <FormFieldInput
                field={field}
                value={value}
                inputId={`form-field-${field.id}`}
                onChange={(nextValue) => handleFieldChange(field.id, nextValue)}
                onBlur={() => handleFieldBlur(field.id)}
                error={error}
                sx={inputSx}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default FormEntryRenderer;

function isAddressValue(value: unknown): value is AddressValue {
  return value !== null
    && value !== undefined
    && typeof value === 'object'
    && 'addressLine1' in value
    && 'city' in value
    && 'postalCode' in value;
}
